// @ts-nocheck
import { uniq } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonUI, Checkbox, TableListV2, Tag } from 'sonata-react-components';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { CampaignStepsMenu } from '@/components/CampaignStepsMenu';
import { formatAdsFormat } from '@/helpers/reporting.helpers';
import { reportingToggleCampaignLinesInfo } from '@/store/actions/reporting.actions';
import { getReportingCampaignLinesInfo } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { CampaignsListStyle } from './campaignsList.style';

import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { BidModelInterface } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

function CustomNameCell({ cell }: { cell: any }) {
  const { props, value } = cell;
  return (
    <div {...props} title={value}>
      {value}
    </div>
  );
}

function CustomAdvertiserCell({ cell }: { cell: any }) {
  const { props, value } = cell;
  return (
    <div {...props}>
      <Tag className="reportingCampaignList__campaignListContent--advertiserTag" id={`customAdvertiserCell-${value}`}>
        <span title={value}>{value}</span>
      </Tag>
    </div>
  );
}

function CustomRegularCell({ cell }: { cell: any }) {
  const { props, value } = cell;
  return (
    <div {...props} title={value}>
      {value}
    </div>
  );
}

function CustomBoldCell({ cell }: { cell: any }) {
  const { props, value } = cell;
  return (
    <div {...props} title={value}>
      {value}
    </div>
  );
}

function CustomButtonCell({ cell }: { cell: any }) {
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const editCampaignPermission = checkUserResourcePermissions('campaignEdition', user, userRoles);
  const { props, value } = cell;
  return (
    <div>
      {editCampaignPermission ? (
        <>
          <div {...props} data-for={`tooltipRef_${value.id}`} data-tip>
            <ButtonUI
              className="table__button"
              icon="far fa-edit"
              iconPosition="left"
              id="reportingCampaignList__editButton"
              shape="link"
              size="medium"
              text=""
              type="secondary"
              visible
            />
          </div>
          <CampaignStepsMenu
            campaignIds={[value.id]}
            campaignTypes={[value.type]}
            place="left"
            tooltipId={`tooltipRef_${value.id}`}
          />
        </>
      ) : (
        <div className="table__buttonCell" />
      )}
    </div>
  );
}

const numOfCampaignLinesToShow = 4;

type Props = {
  currencyInfo: BaseCurrencyInterface;
  dataSelectedCampaigns: CampaignItem[];
};

function CampaignsList({ currencyInfo, dataSelectedCampaigns }: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const cids = useSelector(getCampaigns);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const showCampaignLinesInfo = useSelector(getReportingCampaignLinesInfo);
  const dataCampaigns = React.useMemo(() => {
    return uniq(dataSelectedCampaigns).filter(c => cids.includes(c.id));
  }, [dataSelectedCampaigns]);

  const budgetPermissions = checkUserResourcePermissions('reportingBudget', user, userRoles);
  const pricingPermissions = checkUserResourcePermissions('reportingPricing', user, userRoles);

  const [{ expanded, showButton }, setState] = useState(() => ({
    expanded: false,
    showButton: dataCampaigns.length > numOfCampaignLinesToShow
  }));

  const getAdvertiser = (advertiser: Record<string, any>) => {
    if (advertiser === null) {
      return formatMessage({
        defaultMessage: 'No Advertiser',
        id: 'reporting.header.multi.noAdvertiser'
      });
    }
    return advertiser.name;
  };
  const getFormattedBudget = (budget: number) => {
    return normalizeWithUnit(budget, currencyInfo, 2);
  };

  const getFormattedGoal = (goal: number) => {
    return goal ? normalize(goal, 0, currencyInfo.countries![0]) : 'N/A';
  };

  const getFormattedGoalType = (goalType: string) => {
    return goalType
      ? formatMessage({
          defaultMessage: goalType,
          id: `reporting.header.goals.${goalType.toLowerCase()}`
        })
      : formatMessage({
          defaultMessage: 'No goal defined',
          id: 'reporting.header.single.details.noGoal'
        });
  };

  const getFormattedDates = (beginDate: string, endDate: string) => {
    const formattedBeginDate = moment(beginDate).utc().format('DD/MM/YYYY');
    const formattedEndDate = moment(endDate).utc().format('DD/MM/YYYY');
    return `${formattedBeginDate} - ${formattedEndDate}`;
  };

  const getFormattedPricing = (pricing: BidModelInterface) => {
    const {
      bid: { price, pricingModel }
    } = pricing;
    const formattedPricingModel = pricingModel.replace('-', '').toUpperCase();
    const formattedPricingValue = formattedPricingModel.toLowerCase().includes('cpm') ? price * 1000 : price;
    const formattedPricing = normalizeWithUnit(formattedPricingValue, currencyInfo, 2);
    return `${formattedPricingModel} - ${formattedPricing}`;
  };

  const getFormattedCountries = (countries: string[]) => {
    return countries.join(', ').toLocaleUpperCase();
  };

  const getArrayFormattedByKey = (arr: Record<string, any>[], key: string) => {
    return uniq(arr.map(el => el[key])).join(', ');
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Cell: CustomNameCell,
        Header: 'Campaign Name',
        accessor: 'name',
        className: 'reportingCampaignList__campaignListContent--name',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--name'
      },
      {
        Cell: CustomRegularCell,
        Header: 'Country',
        accessor: 'countries',
        className: 'reportingCampaignList__campaignListContent--regular--country',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--regular--country'
      },
      {
        Cell: CustomRegularCell,
        Header: 'Start and end date',
        accessor: 'dates',
        className: 'reportingCampaignList__campaignListContent--regular--dates',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--regular--dates'
      },
      {
        Cell: CustomAdvertiserCell,
        Header: 'Advertiser',
        accessor: 'advertiser',
        className: 'reportingCampaignList__campaignListContent--advertiser',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--advertiser'
      },
      {
        Cell: CustomBoldCell,
        Header: 'Goal',
        accessor: 'goal',
        className: 'reportingCampaignList__campaignListContent--bold--goal',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--bold--goal'
      },
      {
        Cell: CustomRegularCell,
        Header: 'GoalType',
        accessor: 'goalType',
        className: 'reportingCampaignList__campaignListContent--regular--goalType',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--regular--goalType'
      },
      {
        Cell: CustomRegularCell,
        Header: formatMessage({
          defaultMessage: 'Ad type',
          id: 'reporting.header.multi.list.adType'
        }),
        accessor: 'adFormat',
        className: 'reportingCampaignList__campaignListContent--regular--ad-format',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--regular--ad-format'
      },
      {
        Cell: CustomButtonCell,
        accessor: 'btn',
        className: 'reportingCampaignList__campaignListContent--button',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--button'
      }
    ];

    if (budgetPermissions) {
      baseColumns.splice(6, 0, {
        Cell: CustomBoldCell,
        Header: 'Budget',
        accessor: 'budget',
        className: 'reportingCampaignList__campaignListContent--bold--budget',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--bold--budget'
      });
    }

    if (pricingPermissions) {
      baseColumns.splice(7, 0, {
        Cell: CustomRegularCell,
        Header: 'Pricing',
        accessor: 'pricing',
        className: 'reportingCampaignList__campaignListContent--regular--pricing',
        customCell: true,
        headerClassName: 'reportingCampaignList__campaignListHeader--regular--pricing'
      });
    }

    return baseColumns;
  }, [budgetPermissions, pricingPermissions]);

  const config: React.ComponentProps<typeof TableListV2>['config'] = {
    globalFilter: {
      getDataFiltered: React.useCallback(
        newData => {
          setState(prevState => ({
            ...prevState,
            showButton: newData.length === dataCampaigns.length
          }));
        },
        [dataCampaigns]
      ),
      headerTextAlign: 'left',
      show: expanded
    },
    pagination: {
      show: false
    },
    type: 'table'
  };

  const generateData = useMemo(() => {
    const result = expanded ? dataCampaigns : dataCampaigns.slice(0, numOfCampaignLinesToShow);
    return result.map(campaign => {
      const {
        ads,
        beginDate,
        bidModels,
        budget,
        countries,
        endDate,
        mediaAdvertiser,
        name,
        overallGoal = {
          target: '',
          type: ''
        },
        venues
      } = campaign;
      const adFormats = getArrayFormattedByKey(ads, 'adFormat');
      return {
        adFormat: formatAdsFormat(!Array.isArray(adFormats) ? [adFormats] : adFormats),
        advertiser: getAdvertiser(mediaAdvertiser),
        btn: campaign,
        budget: getFormattedBudget(budget),
        countries: getFormattedCountries(countries),
        dates: getFormattedDates(beginDate, endDate),
        goal: getFormattedGoal(overallGoal.target),
        goalType: getFormattedGoalType(overallGoal.type),
        name,
        pricing: getFormattedPricing(bidModels[0]),
        venueType: getArrayFormattedByKey(venues, 'type')
      };
    });
  }, [expanded, dataCampaigns]);

  const onClickShowMore = () => {
    setState(prevState => ({
      ...prevState,
      expanded: !expanded
    }));
  };

  const toggleCampaignLines = () => {
    dispatch(reportingToggleCampaignLinesInfo(!showCampaignLinesInfo));
  };

  return (
    <>
      <div className="reportingShortcuts__element--multi" id="reportingCampaignInfo" />
      <CampaignsListStyle className="reportingCampaignListContainer">
        <div className="reportingCampaignListContainer__containerSwitch">
          <div className="reportingCampaignListContainer__wrapperSwitch">
            <Checkbox
              active={showCampaignLinesInfo}
              classes="reportingCampaignListContainer__switch"
              id="campaign_lines_info_button"
              labelColor="#505969"
              labelText={formatMessage({
                defaultMessage: 'Campaign lines info',
                id: 'reporting.header.campaignList.switchButton'
              })}
              onClickAction={toggleCampaignLines}
              type="switch"
            />
          </div>
        </div>
        {showCampaignLinesInfo && (
          <div className="reportingCampaignListContainer__tableContainer">
            <div className="reportingCampaignListContainer__wrapperTable">
              <div className="reportingCampaignListContainer__contentTable">
                <div className="reportingCampaignList">
                  <TableListV2 columns={columns} config={config} data={generateData} key={generateData.length} />
                </div>
                {showButton && (
                  <div className="reportingCampaignListContainer__button">
                    <ButtonUI
                      id="show_more_campaign_list_button"
                      onClick={onClickShowMore}
                      shape="link"
                      size="small"
                      text={formatMessage({
                        defaultMessage: expanded ? 'Show Less' : 'Show more',
                        id: `reporting.header.campaignList.${expanded ? 'buttonShowLess' : 'buttonShowMore'}`
                      })}
                      type="secondary"
                      visible
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CampaignsListStyle>
    </>
  );
}

export default CampaignsList;
